/**
 * API base for the survey app.
 *
 * The app is normally served from front.servefirst.co.uk (or front-stage…) and
 * calls the API at REACT_APP_BASE_URL, baked at build time. Customers with the
 * CSAT custom-domain add-on load the same build from THEIR hostname (e.g.
 * csat.theircompany.com) via the multi-tenant CloudFront distribution, which
 * routes /api/* on that host to sf-api and strips the prefix. On those hosts we
 * must call same-origin /api so the browser never makes a cross-origin request
 * (the API's CORS allow-list is unchanged and does not know customer hosts).
 *
 * SF-4600: the same build is also served from our servefirst.ai survey hosts
 * (front.servefirst.ai, front-stage.servefirst.ai, dev-front.servefirst.ai).
 * Those are platform hosts too, and call the API on the matching .ai API host
 * (REACT_APP_BASE_URL with its servefirst.co.uk suffix swapped for
 * servefirst.ai, e.g. devapi.servefirst.co.uk -> devapi.servefirst.ai).
 *
 * Rule: if the page host is one of OUR known hosts, use REACT_APP_BASE_URL
 * (swapped to .ai on .ai pages); otherwise use "/api". The host match is
 * exact (anchored patterns), never *.servefirst.ai: customer survey domains on
 * csat.servefirst.ai and short links on go.servefirst.ai must stay customer
 * hosts.
 */

// front.servefirst.co.uk, front-stage.servefirst.co.uk, front-dev.servefirst.co.uk
const FRONT_CO_UK_HOST = /^front(-[a-z0-9]+)?\.servefirst\.co\.uk$/;
// front.servefirst.ai, front-stage.servefirst.ai, and the dev host, which is
// named dev-front (not front-dev) on .ai.
const FRONT_AI_HOST = /^(front(-[a-z0-9]+)?|dev-front)\.servefirst\.ai$/;

function normalizeHost(host) {
  return String(host || "").toLowerCase();
}

/** True for our own survey hosts on servefirst.ai. */
export function isAiPlatformHost(host) {
  return FRONT_AI_HOST.test(normalizeHost(host));
}

/** Hosts that keep using the baked absolute API URL. */
export function isPlatformHost(host) {
  const h = normalizeHost(host);
  if (!h) return true; // SSR / tests without a window: behave as before
  if (
    h === "localhost" ||
    h.startsWith("localhost:") ||
    h.startsWith("127.0.0.1")
  )
    return true;
  // Our front hosts on both domains, and the raw CloudFront/S3 hostnames the
  // build is sometimes previewed from.
  return (
    FRONT_CO_UK_HOST.test(h) ||
    FRONT_AI_HOST.test(h) ||
    h.endsWith(".cloudfront.net") ||
    h.endsWith(".amazonaws.com")
  );
}

/**
 * Swap a servefirst.co.uk API URL to its servefirst.ai twin
 * (https://devapi.servefirst.co.uk -> https://devapi.servefirst.ai).
 * Anything else (localhost, an already-.ai URL, an unparsable value) is
 * returned unchanged.
 */
export function toAiApiBase(baseUrl) {
  const base = String(baseUrl || "");
  try {
    const url = new URL(base);
    const host = url.hostname.toLowerCase();
    if (host === "servefirst.co.uk" || host.endsWith(".servefirst.co.uk")) {
      url.hostname = `${host.slice(0, -"servefirst.co.uk".length)}servefirst.ai`;
      return url.toString().replace(/\/$/, "");
    }
  } catch (_e) {
    // not an absolute URL: leave it alone
  }
  return base;
}

/**
 * @param {string} [host]  defaults to window.location.host
 * @returns {string} base URL WITHOUT trailing slash: absolute for platform hosts, "/api" for customer hosts
 */
export function getApiBase(host) {
  const h =
    host !== undefined
      ? host
      : typeof window !== "undefined"
        ? window.location.host
        : "";
  if (isPlatformHost(h)) {
    const base = String(process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
    return isAiPlatformHost(h) ? toAiApiBase(base) : base;
  }
  return "/api";
}

export default getApiBase;
